import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { analyticsAPI, botsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Bot, Users, MessageSquare, TrendingUp, Plus, ArrowRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';

function StatCard({ label, value, icon: Icon, color, sub }) {
  return (
    <div className="stat-card" style={{ display:'flex',flexDirection:'column',gap:12 }}>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between' }}>
        <span className="stat-label">{label}</span>
        <div style={{ width:36,height:36,borderRadius:'var(--radius)',background:`${color}18`,display:'flex',alignItems:'center',justifyContent:'center',color }}>
          <Icon size={18}/>
        </div>
      </div>
      <div className="stat-value">{value ?? '—'}</div>
      {sub && <div style={{ fontSize:12,color:'var(--text3)' }}>{sub}</div>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'var(--surface)',border:'1px solid var(--border2)',borderRadius:'var(--radius)',padding:'10px 14px',fontSize:13 }}>
      <div style={{ color:'var(--text3)',marginBottom:6 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color:p.color,display:'flex',gap:6,alignItems:'center' }}>
          <span style={{ fontWeight:600 }}>{p.value}</span> {p.name}
        </div>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [chart, setChart]       = useState([]);
  const [bots, setBots]         = useState([]);
  const [days, setDays]         = useState(7);

  useEffect(() => {
    analyticsAPI.overview().then(r => setOverview(r.data.overview)).catch(()=>{});
    botsAPI.list().then(r => setBots(r.data.bots || [])).catch(()=>{});
  }, []);

  useEffect(() => {
    analyticsAPI.chart({ days }).then(r => setChart(r.data.chart || [])).catch(()=>{});
  }, [days]);

  const chartData = chart.map(d => ({
    ...d,
    date: format(parseISO(d.date), 'MMM d')
  }));

  return (
    <div>
      <div className="page-header" style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:12 }}>
        <div>
          <h1>Dashboard</h1>
          <p>Welcome back, {user?.name?.split(' ')[0]}! Here's what's happening.</p>
        </div>
        <Link to="/bots" className="btn btn-primary">
          <Plus size={16}/> New Chatbot
        </Link>
      </div>

      {/* Stats */}
      <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:16,marginBottom:28 }}>
        <StatCard label="Total Chatbots"    value={overview?.totalBots}          icon={Bot}          color="var(--brand)" sub={`${user?.plan} plan`}/>
        <StatCard label="Total Leads"       value={overview?.totalLeads}         icon={Users}        color="var(--blue)"  sub={`${overview?.todayLeads || 0} today`}/>
        <StatCard label="Conversations"     value={overview?.totalConversations} icon={MessageSquare} color="var(--amber)" sub={`${overview?.todayConversations || 0} today`}/>
        <StatCard label="New Leads"         value={overview?.newLeads}           icon={TrendingUp}   color="#c97ff5"      sub="Awaiting contact"/>
      </div>

      {/* Chart */}
      <div className="card" style={{ marginBottom:24 }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20 }}>
          <h2 style={{ fontFamily:'var(--font-head)',fontSize:16,fontWeight:700 }}>Activity Overview</h2>
          <div className="tabs">
            {[7,14,30].map(d => (
              <button key={d} className={`tab${days===d?' active':''}`} onClick={() => setDays(d)}>{d}d</button>
            ))}
          </div>
        </div>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--brand)" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="var(--brand)" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="lg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--blue)" stopOpacity={0.25}/>
                  <stop offset="95%" stopColor="var(--blue)" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
              <XAxis dataKey="date" tick={{ fill:'var(--text3)',fontSize:12 }} axisLine={false} tickLine={false}/>
              <YAxis tick={{ fill:'var(--text3)',fontSize:12 }} axisLine={false} tickLine={false} width={32}/>
              <Tooltip content={<CustomTooltip/>}/>
              <Area type="monotone" dataKey="conversations" name="conversations" stroke="var(--brand)" strokeWidth={2} fill="url(#cg)"/>
              <Area type="monotone" dataKey="leads"         name="leads"         stroke="var(--blue)"  strokeWidth={2} fill="url(#lg)"/>
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ height:220,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text3)',fontSize:14 }}>
            No data yet — start by creating a chatbot
          </div>
        )}
      </div>

      {/* Recent bots */}
      {bots.length > 0 && (
        <div className="card">
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16 }}>
            <h2 style={{ fontFamily:'var(--font-head)',fontSize:16,fontWeight:700 }}>Your Chatbots</h2>
            <Link to="/bots" className="btn btn-ghost btn-sm" style={{ color:'var(--brand)' }}>View all <ArrowRight size={14}/></Link>
          </div>
          <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
            {bots.slice(0,4).map(bot => (
              <div key={bot._id} style={{ display:'flex',alignItems:'center',gap:14,padding:'10px 0',borderBottom:'1px solid var(--border)' }}>
                <div style={{ width:38,height:38,borderRadius:'var(--radius)',background:'var(--surface2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18 }}>
                  {bot.avatarEmoji || '🤖'}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:500,fontSize:14 }}>{bot.name}</div>
                  <div style={{ fontSize:12,color:'var(--text3)' }}>{bot.knowledge?.length || 0} knowledge entries</div>
                </div>
                <span className={`badge badge-${bot.isActive?'green':'gray'}`}>
                  {bot.isActive ? 'Active' : 'Inactive'}
                </span>
                <Link to={`/bots/${bot._id}/edit`} className="btn btn-ghost btn-sm btn-icon"><ArrowRight size={15}/></Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {bots.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <div style={{ fontSize:48 }}>🤖</div>
            <h3>No chatbots yet</h3>
            <p>Create your first chatbot and start capturing leads from your website automatically.</p>
            <Link to="/bots" className="btn btn-primary" style={{ marginTop:8 }}>
              <Plus size={16}/> Create your first chatbot
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
